import { FormService, Logger } from '@core/services';
import { untilDestroyed } from '@ngneat/until-destroy';
import { DropDownItem } from '@core/types/Dropdown.type';
import { ConfirmationDialogType, DeleteConfirmationOptions, WorkShiftsTab } from '@core/enums';
import { EventEntity, UserEntity, WorkShiftEntity } from '@core/entities';
import { inject } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgxBottomSheetService } from 'ngx-bottom-sheet';
import { DialogRef, DialogService } from '@ngneat/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { HotToastService } from '@ngxpert/hot-toast';
import { PermissionService } from '@auth';
import { UseClub, UseWorkShift } from '@core/usecases';
import { DateTimeUtility, isMobileView } from '@core/utils';
import { DefaultDeleteDialogConfig, MDeleteConfirmationDialogComponent } from '@mobile/_shared/dialogs';
import { ConfirmationDialogComponent } from '@web/_shared/dialogs';
import { ButtonComponent } from '@shared/components/ui';

const log = new Logger('Work shift Form Composition');

export class WorkShiftFormComposition extends FormService {
  days: DropDownItem[] = [];
  selectedTab = WorkShiftsTab.Form;
  events: EventEntity[] = [];
  currentSelectedEvent: EventEntity;
  currentSelectedShift: WorkShiftEntity;
  selectedImage: File;

  teamUsers: UserEntity[] = [];

  protected readonly _bottomSheetService: NgxBottomSheetService;
  protected readonly _dialogService: DialogService;
  protected _dialogRef: DialogRef;
  protected readonly _translateService = inject(TranslateService);
  protected readonly _router = inject(Router);
  protected readonly _toast = inject(HotToastService);
  protected readonly _permissionService = inject(PermissionService);

  private readonly _useWorkShift = new UseWorkShift();
  private readonly _useClub = new UseClub();

  private readonly _formBuilder = inject(FormBuilder);

  constructor() {
    super();
    this.days = DateTimeUtility.getWeekDays(this._translateService) as DropDownItem[];

    if (!isMobileView()) {
      this._dialogService = inject(DialogService);
    } else {
      this._bottomSheetService = inject(NgxBottomSheetService);
    }
  }

  // Getter for shifts FormArray
  get shiftsFormArray(): FormArray {
    return this.form?.get('shifts') as FormArray;
  }

  get selectedEventDetails() {
    if (this.currentSelectedEvent) {
      return this.currentSelectedEvent;
    }

    if (this.form.value.eventId) {
      return this.events.find((event) => event.id === this.form.value.eventId);
    } else {
      return null;
    }
  }

  initDataForForm() {
    this._initForm();
    this._fetchAllEventsWithoutShifts();
  }

  addShift() {
    this.shiftsFormArray.push(this.createShift());
  }

  removeShift(index: number) {
    this.shiftsFormArray.removeAt(index);
  }

  onAddShift() {
    if (this.shiftsFormArray.valid) {
      this.addShift();
    } else {
      this.shiftsFormArray.markAllAsTouched();
    }
  }

  createShift(): FormGroup {
    const previousSelectedDate = this.shiftsFormArray?.length ? this.shiftsFormArray?.at(this.shiftsFormArray.length - 1).value?.date : null;
    return this._formBuilder.group({
      id: [null],
      date: [previousSelectedDate, [Validators.required]],
      startTime: [null, [Validators.required]],
      endTime: [null, [Validators.required]],
      title: [null, [Validators.required]],
      desc: [null, [Validators.required]],
      image: [null],
      shiftAssignUser: [null, [Validators.required]],
    });
  }

  save(btnRef?: ButtonComponent) {
    if (this.form.invalid) {
      this.markAllInvalidControlsAndScrollToFirst(this.form);
      return;
    }

    const formValues = this.form.value;
    const shifts = formValues.shifts.map((shift: any) => {
      shift.shiftAssignUser = shift.shiftAssignUser.map((userId: number) => {
        const matchedUser = this.teamUsers.find((user) => user.id === userId);
        if (matchedUser) {
          return {
            id: matchedUser.id,
            name: matchedUser.fullName,
          } as UserEntity;
        }
        return null;
      });

      return this.removeNullObjects(shift);
    });

    const formData = this._convertShiftsToFormData(shifts);

    formData.append('event_id', formValues.eventId);

    const service$ = this.currentSelectedEvent && this.currentSelectedEvent?.id ? this._useWorkShift.updateShifts(formData, this.currentSelectedEvent.id) : this._useWorkShift.createShifts(formData);

    this.call(service$, btnRef)
      .then((response) => {
        if (response) {
          if (this.currentSelectedEvent && this.currentSelectedEvent.id) this._fetchShiftsByEventId(this.currentSelectedEvent.id);
          const message = this.currentSelectedEvent && this.currentSelectedEvent?.id ? 'Update Successful' : 'Created Successfully';
          this._toast.success(this._translateService.instant(message));
          if (this.currentSelectedShift && this.currentSelectedShift.id) {
            this.selectedTab = WorkShiftsTab.Shifts;
            this.currentSelectedShift = null;
          } else {
            if (!isMobileView()) {
              this._dialogService.closeAll();
            } else {
              this._bottomSheetService.closeAll();
            }
          }
          this._reloadCurrentRoute();
        } else {
          this._toast.error(this._translateService.instant('Something went wrong'));
        }
      })
      .catch((err) => {
        this._toast.error(this.error, { duration: 5000 });
        log.error('Error while creating or updating workshifts', err);
      });
  }

  editShift(shift: WorkShiftEntity) {
    this.selectedTab = WorkShiftsTab.Form;
    this._patchShift(shift);
  }

  deleteShift(shift: WorkShiftEntity) {
    if (!shift.id) return;

    if (isMobileView()) {
      const confirmDeleteSheet = this._bottomSheetService.open(MDeleteConfirmationDialogComponent, {
        ...DefaultDeleteDialogConfig,
        data: {
          text: shift.title + '?',
        },
      });

      confirmDeleteSheet.afterClosed$.pipe(untilDestroyed(this)).subscribe({
        next: (res) => {
          switch (res) {
            case DeleteConfirmationOptions.DELETE:
              if (res) {
                this.call(this._useWorkShift.deleteShift(shift.id)).then(() => {
                  this._toast.success(this._translateService.instant('Delete Successful'));
                  this._fetchShiftsByEventId(this.currentSelectedEvent.id);
                });
              }
              break;
            default:
              break;
          }
        },
      });
    } else {
      const confirmationDelete = this._dialogService.open(ConfirmationDialogComponent, {
        size: 'md',
        closeButton: false,
        windowClass: 'dialog-overflow-unset',
        data: {
          type: ConfirmationDialogType.DELETE,
          emphasize: shift.title,
        },
      });
      confirmationDelete.afterClosed$.pipe(untilDestroyed(this)).subscribe({
        next: (result: any) => {
          if (result) {
            this.call(this._useWorkShift.deleteShift(shift.id)).then(() => {
              this._toast.success(this._translateService.instant('Delete Successful'));
              this._fetchShiftsByEventId(this.currentSelectedEvent.id);
            });
          }
        },
      });
    }

    this._reloadCurrentRoute();
  }

  convertShiftsToFormDataAndAppend(shiftsArray: WorkShiftEntity[]): FormData {
    return this._convertShiftsToFormData(shiftsArray);
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // ------------------------------------------ Private Protected Functions ----------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  protected _fetchSelectedEventDetails() {
    const currentShifts = this.currentSelectedEvent.eventShifts;
    this.isLoading = true;
    this._useWorkShift
      .getEventDetailsForShift(this.currentSelectedEvent.id)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res: EventEntity) => {
          if (res) {
            this.currentSelectedEvent = res;
            this.currentSelectedEvent.eventShifts = currentShifts;
            this._fetchAllTeamUsers();
            this.isLoading = false;
          }
        },
      });
  }

  private _fetchAllEventsWithoutShifts() {
    this._useWorkShift
      .getAllEventsWithoutShifts()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res: EventEntity[]) => {
          if (res) {
            this.events = res;
            this.isLoading = false;
            this._fetchAllTeamUsers();
          }
        },
      });
  }

  private _fetchAllTeamUsers() {
    this._useClub.getClubMembers().then((participants) => {
      if (participants) {
        this.teamUsers = participants;
        const matchedParticipants = (participant: any) => {
          const matchedItem = participants.find((member) => member.id === participant.userId);
          if (matchedItem) {
            participant.user = matchedItem;
          }
          return participant;
        };
        this.events.forEach((event) => {
          event.participants.forEach(matchedParticipants);
        });

        if (this.currentSelectedEvent && this.currentSelectedEvent.id && this.currentSelectedEvent.participants && this.currentSelectedEvent.participants.length) {
          // iterate both event.participants and this.teamUsers and match the user id and assign user from teamUsers to participant user property
          this.currentSelectedEvent.participants.forEach(matchedParticipants);
        }
      }
    });
  }

  private _fetchShiftsByEventId(eventId: number) {
    this.isLoading = true;
    this.form.reset();
    this.form.disable();
    this._useWorkShift
      .getEventWithShiftsByEventId(eventId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res: EventEntity) => {
          if (res) {
            this.currentSelectedEvent.eventShifts = res.eventShifts;
            this.isLoading = false;
            this._initForm();
            this.form.patchValue({
              eventId: eventId,
            });
          }
        },
      });
  }

  private _initForm() {
    this.form = this._formBuilder.group({
      eventId: [null, [Validators.required]],
      shifts: this._formBuilder.array([]),
    });
    this.addShift();
  }

  private _patchShift(shift: WorkShiftEntity) {
    this.currentSelectedShift = shift;
    this.shiftsFormArray.clear();
    const users = shift.shiftAssign.map((user) => user.userId);
    this.shiftsFormArray.push(
      this._formBuilder.group({
        id: [shift.id],
        date: [DateTimeUtility.getDateOnly(shift.date), [Validators.required]],
        startTime: [shift.startTime, [Validators.required]],
        endTime: [shift.endTime, [Validators.required]],
        title: [shift.title, [Validators.required]],
        desc: [shift.description, [Validators.required]],
        image: [shift.imageUrl],
        shiftAssignUser: [users],
      }),
    );
  }

  private _convertShiftsToFormData(shiftsArray: WorkShiftEntity[]): FormData {
    const formData = new FormData();

    shiftsArray.forEach((shift, index) => {
      if (shift.id) {
        formData.append(`shifts[${index}][id]`, shift.id.toString());
      }

      formData.append(`shifts[${index}][date]`, shift.date);
      formData.append(`shifts[${index}][start_time]`, shift.startTime);
      formData.append(`shifts[${index}][end_time]`, shift.endTime);
      formData.append(`shifts[${index}][title]`, shift.title);
      formData.append(`shifts[${index}][desc]`, shift.desc);

      if (shift.image && shift.image instanceof File) {
        formData.append(`shifts[${index}][files]`, shift.image, shift?.image?.name);
      }

      shift.shiftAssignUser.forEach((user, userIndex) => {
        formData.append(`shifts[${index}][shift_assign_user][${userIndex}][id]`, user.id.toString());
        formData.append(`shifts[${index}][shift_assign_user][${userIndex}][name]`, user.name);
      });
    });

    return formData;
  }

  private _reloadCurrentRoute(path?: string) {
    const currentUrl = path || this._router.url;
    this._router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this._router.navigate([currentUrl]);
    });
  }
}
